import {
  DeesElement,
  property,
  html,
  customElement,
  type TemplateResult,
  css,
  cssManager,
  state,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';

import type { Terminal } from 'xterm';
import { themeDefaultStyles } from '../../00theme.js';
import type { IExecutionEnvironment, IShellCommand } from '../../00group-runtime/index.js';
import { WebContainerEnvironment } from '../../00group-runtime/index.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-feedback/dees-actionbar/dees-actionbar.js';
import type { DeesActionbar } from '../../00group-feedback/dees-actionbar/dees-actionbar.js';
import { TerminalTabManager } from './terminal-tab-manager.js';
import type {
  ITerminalTab,
  ICreateTerminalTabOptions,
  TTerminalTabType,
} from './interfaces.js';
import { DeesServiceLibLoader } from '../../../services/index.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-workspace-terminal': DeesWorkspaceTerminal;
  }
}

@customElement('dees-workspace-terminal')
export class DeesWorkspaceTerminal extends DeesElement {
  public static demo = () => {
    const env = new WebContainerEnvironment();
    return html`<dees-workspace-terminal .executionEnvironment=${env}></dees-workspace-terminal>`;
  };
  public static demoGroups = ['Workspace'];

  // INSTANCE
  private resizeObserver: ResizeObserver;
  private tabManager: TerminalTabManager;

  /**
   * The execution environment (required).
   * Use WebContainerEnvironment for browser-based execution.
   */
  @property({ type: Object })
  accessor executionEnvironment: IExecutionEnvironment | null = null;

  @property()
  accessor setupCommand = '';

  /**
   * Environment variables to set in the shell
   */
  @property()
  accessor environmentVariables: { [key: string]: string } = {};

  /**
   * Width of the tab bar in pixels
   */
  @property({ type: Number })
  accessor tabBarWidth: number = 160;

  /**
   * Whether to show the tab bar
   */
  @property({ type: Boolean })
  accessor showTabBar: boolean = true;

  // Tab state
  @state()
  accessor tabs: ITerminalTab[] = [];

  @state()
  accessor activeTabId: string | null = null;

  // Theme subscription for dynamic theme updates
  private terminalThemeSubscription: any = null;
  private isBright: boolean = false;

  // Actionbar reference for terminal-context notifications
  private terminalActionbar: DeesActionbar | null = null;

  private lifecycleAbortController = new AbortController();

  /**
   * Promise that resolves when the environment is ready.
   * @deprecated Use executionEnvironment directly
   */
  private environmentDeferred = new domtools.plugins.smartpromise.Deferred<IExecutionEnvironment>();
  public environmentPromise = this.environmentDeferred.promise;

  constructor() {
    super();
    this.tabManager = new TerminalTabManager();
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        background: var(--dees-color-bg-primary);
        display: flex;
        flex-direction: row;
        box-sizing: border-box;
        height: 100%;
        width: 100%;
      }

      * {
        box-sizing: border-box;
      }

      .terminal-container {
        display: flex;
        flex-direction: row;
        width: 100%;
        height: 100%;
      }

      .terminal-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: var(--dees-color-bg-primary);
      }

      #active-terminal-container {
        flex: 1;
        position: relative;
        min-height: 0;
        margin: var(--dees-spacing-sm);
      }

      .terminal-content dees-actionbar {
        flex-shrink: 0;
      }

      /* Tab bar on the right side */
      .tab-bar {
        display: flex;
        flex-direction: column;
        background: var(--dees-color-bg-tertiary);
        border-left: 1px solid var(--dees-color-border-subtle);
        flex-shrink: 0;
        overflow: hidden;
      }

      .tab-bar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--dees-color-text-muted);
      }

      .tab-bar-title {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .tab-bar-actions {
        display: flex;
        gap: 2px;
      }

      .tab-action {
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--dees-radius-sm);
        cursor: pointer;
        color: var(--dees-color-text-muted);
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .tab-action:hover {
        background: color-mix(in srgb, var(--dees-color-text-primary) 8%, transparent);
        color: var(--dees-color-text-secondary);
      }

      .tab-list {
        flex: 1;
        overflow-y: auto;
        padding: 4px;
      }

      .terminal-tab {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        margin-bottom: 2px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 12px;
        color: var(--dees-color-text-muted);
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .terminal-tab:hover {
        background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
        color: var(--dees-color-text-secondary);
      }

      /* Selected terminal uses the same elevated pill as the editor/panel tabs
         (gray in dark, white in light) with primary text — keeps the whole
         terminal area on one selection language. */
      .terminal-tab.active,
      .terminal-tab.active:hover {
        background: #5a5a5e;
        color: var(--dees-color-text-primary);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
      }

      :host([gobright]) .terminal-tab.active,
      :host([gobright]) .terminal-tab.active:hover {
        background: #ffffff;
        color: var(--dees-color-text-primary);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16), 0 0 1px rgba(0, 0, 0, 0.1);
      }

      .terminal-tab.exited {
        opacity: 0.7;
      }

      .tab-icon {
        flex-shrink: 0;
      }

      .tab-icon.running {
        color: var(--dees-color-text-success);
      }

      .tab-label {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .exit-badge {
        font-size: 10px;
        padding: 1px 5px;
        border-radius: 8px;
        font-weight: 600;
        flex-shrink: 0;
      }

      .exit-badge.success {
        background: var(--dees-color-badge-success-bg);
        color: var(--dees-color-badge-success-fg);
      }

      .exit-badge.error {
        background: var(--dees-color-badge-error-bg);
        color: var(--dees-color-badge-error-fg);
      }

      .tab-close {
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--dees-radius-sm);
        opacity: 0;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        flex-shrink: 0;
      }

      .terminal-tab:hover .tab-close {
        opacity: 0.6;
      }

      .tab-close:hover {
        opacity: 1 !important;
        background: color-mix(in srgb, var(--dees-color-text-primary) 15%, transparent);
        color: var(--dees-color-text-error);
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--dees-color-text-muted);
        font-size: 13px;
        gap: 8px;
      }

      /* xterm.js styles */
      .xterm {
        font-feature-settings: 'liga' 0;
        position: relative;
        user-select: none;
        -ms-user-select: none;
        -webkit-user-select: none;
      }

      .xterm.focus,
      .xterm:focus {
        outline: none;
      }

      .xterm .xterm-helpers {
        position: absolute;
        top: 0;
        z-index: 5;
      }

      .xterm .xterm-helper-textarea {
        padding: 0;
        border: 0;
        margin: 0;
        position: absolute;
        opacity: 0;
        left: -9999em;
        top: 0;
        width: 0;
        height: 0;
        z-index: -5;
        white-space: nowrap;
        overflow: hidden;
        resize: none;
      }

      .xterm .composition-view {
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        display: none;
        position: absolute;
        white-space: nowrap;
        z-index: 1;
      }

      .xterm .composition-view.active {
        display: block;
      }

      .xterm .xterm-viewport {
        background-color: var(--dees-color-bg-primary);
        overflow-y: scroll;
        cursor: default;
        position: absolute;
        right: 0;
        left: 0;
        top: 0;
        bottom: 0;
      }

      .xterm .xterm-screen {
        position: relative;
      }

      .xterm .xterm-screen canvas {
        position: absolute;
        left: 0;
        top: 0;
      }

      .xterm .xterm-scroll-area {
        visibility: hidden;
      }

      .xterm-char-measure-element {
        display: inline-block;
        visibility: hidden;
        position: absolute;
        top: 0;
        left: -9999em;
        line-height: normal;
      }

      .xterm {
        cursor: text;
      }

      .xterm.enable-mouse-events {
        cursor: default;
      }

      .xterm.xterm-cursor-pointer {
        cursor: pointer;
      }

      .xterm.column-select.focus {
        cursor: crosshair;
      }

      .xterm .xterm-accessibility,
      .xterm .xterm-message {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        right: 0;
        z-index: 10;
        color: transparent;
      }

      .xterm .live-region {
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      }

      .xterm-dim {
        opacity: 0.5;
      }

      .xterm-underline {
        text-decoration: underline;
      }
    `,
  ];

  public render(): TemplateResult {
    const activeTab = this.activeTabId ? this.tabManager.getTab(this.activeTabId) : null;

    return html`
      <div class="terminal-container">
        <!-- Main terminal area -->
        <div class="terminal-content">
          ${activeTab ? html`
            <div id="active-terminal-container"></div>
          ` : html`
            <div class="empty-state">
              <dees-icon .icon=${'lucide:terminal'} iconSize="32"></dees-icon>
              <span>No terminal open</span>
            </div>
          `}
          <dees-actionbar></dees-actionbar>
        </div>

        <!-- Vertical tab bar on the right -->
        ${this.showTabBar ? html`
          <div class="tab-bar" style="width: ${this.tabBarWidth}px">
            <div class="tab-bar-header">
              <span class="tab-bar-title">Terminals</span>
              <div class="tab-bar-actions">
                <div class="tab-action" @click=${this.handleAddTab} title="New Terminal">
                  <dees-icon .icon=${'lucide:plus'} iconSize="14"></dees-icon>
                </div>
              </div>
            </div>

            <div class="tab-list">
              ${this.tabs.map(tab => html`
                <div
                  class="terminal-tab ${tab.id === this.activeTabId ? 'active' : ''} ${tab.exited ? 'exited' : ''}"
                  @click=${() => this.switchToTab(tab.id)}
                >
                  <dees-icon
                    .icon=${tab.iconName}
                    iconSize="14"
                    class="tab-icon ${tab.process && !tab.exited ? 'running' : ''}"
                  ></dees-icon>
                  <span class="tab-label">${tab.label}</span>
                  ${tab.exited ? html`
                    <span class="exit-badge ${tab.exitCode === 0 ? 'success' : 'error'}">
                      ${tab.exitCode}
                    </span>
                  ` : ''}
                  ${tab.closeable ? html`
                    <span class="tab-close" @click=${(e: Event) => this.handleTabClose(e, tab.id)}>
                      <dees-icon .icon=${'lucide:x'} iconSize="12"></dees-icon>
                    </span>
                  ` : ''}
                </div>
              `)}
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  public async firstUpdated(): Promise<void> {
    const domtoolsInstance = await this.domtoolsPromise;
    if (!this.isConnected || this.lifecycleAbortController.signal.aborted) return;

    // Get current theme
    this.isBright = domtoolsInstance.themeManager.goBrightBoolean;

    // Load xterm from CDN
    const libLoader = DeesServiceLibLoader.getInstance();
    const [xtermBundle, fitAddonBundle] = await Promise.all([
      libLoader.loadXterm(),
      libLoader.loadXtermFitAddon(),
    ]);
    if (!this.isConnected || this.lifecycleAbortController.signal.aborted) return;

    // Initialize tab manager with loaded modules
    this.tabManager.setXtermModules(xtermBundle, fitAddonBundle);

    // Subscribe to theme changes only after async setup is still current.
    this.terminalThemeSubscription = domtoolsInstance.themeManager.themeObservable.subscribe(
      (goBright: boolean) => {
        this.isBright = goBright;
        this.tabManager.updateAllThemes(goBright);
      }
    );

    // Create default shell tab
    await this.createShellTab();
  }

  async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    if (this.lifecycleAbortController.signal.aborted) {
      this.lifecycleAbortController = new AbortController();
    }
    // ResizeObserver is set up in attachTerminalToContainer when the container exists
  }

  async disconnectedCallback(): Promise<void> {
    this.lifecycleAbortController.abort();
    this.resizeObserver.disconnect();
    if (this.terminalThemeSubscription) {
      this.terminalThemeSubscription.unsubscribe();
      this.terminalThemeSubscription = null;
    }
    this.tabManager.disposeAll();
    await super.disconnectedCallback();
  }

  // ========== Tab Management ==========

  private async handleAddTab(): Promise<void> {
    await this.createShellTab();
  }

  private handleTabClose(e: Event, tabId: string): void {
    e.stopPropagation();
    this.closeTab(tabId);
  }

  private switchToTab(tabId: string): void {
    if (tabId === this.activeTabId) return;

    const tab = this.tabManager.getTab(tabId);
    if (!tab) return;

    const previousTabId = this.activeTabId;

    // Detach current terminal from DOM
    if (this.activeTabId) {
      this.detachActiveTerminal();
    }

    // Update active tab
    this.activeTabId = tabId;
    this.tabs = this.tabManager.getAllTabs();

    // Wait for render then attach new terminal
    this.updateComplete.then(() => {
      if (
        !this.isConnected
        || this.lifecycleAbortController.signal.aborted
        || !this.tabManager.hasTab(tab.id)
        || this.activeTabId !== tab.id
      ) {
        return;
      }

      this.attachTerminalToContainer(tab);

      // Dispatch event
      this.dispatchEvent(
        new CustomEvent('tab-switched', {
          bubbles: true,
          composed: true,
          detail: { tabId, previousTabId },
        })
      );
    });
  }

  private detachActiveTerminal(): void {
    const container = this.shadowRoot?.getElementById('active-terminal-container');
    if (container) {
      container.innerHTML = '';
    }
  }

  private attachTerminalToContainer(tab: ITerminalTab): void {
    const container = this.shadowRoot?.getElementById('active-terminal-container');
    if (!container) return;

    // Observe container for resize (handles actionbar appearing/disappearing)
    // ResizeObserver.observe() is idempotent - safe to call multiple times
    this.resizeObserver.observe(container);

    // Clear container
    container.innerHTML = '';

    // Open terminal in container
    tab.terminal.open(container);
    this.fitAndResizeTab(tab);
    tab.terminal.focus();
  }

  public handleResize(): void {
    if (this.activeTabId) {
      const tab = this.tabManager.getTab(this.activeTabId);
      if (tab) {
        this.fitAndResizeTab(tab);
      }
    }
  }

  private fitAndResizeTab(tab: ITerminalTab): void {
    tab.fitAddon.fit();
    if (tab.process && !tab.exited) {
      tab.process.resize?.({
        cols: tab.terminal.cols,
        rows: tab.terminal.rows,
      });
    }
  }

  // ========== Process Management ==========

  private async spawnProcessForTab(
    tab: ITerminalTab,
    command: string,
    args: string[] = []
  ): Promise<void> {
    if (!this.executionEnvironment) {
      tab.terminal.write('\x1b[31m');
      tab.terminal.write('❌ No execution environment available.\r\n');
      tab.terminal.write('\x1b[0m');
      return;
    }

    try {
      // Initialize environment if needed
      if (!this.executionEnvironment.ready) {
        tab.terminal.write('Initializing environment...\r\n');
        await this.executionEnvironment.init();
      }
      if (!this.isConnected || this.lifecycleAbortController.signal.aborted || !this.tabManager.hasTab(tab.id)) {
        return;
      }

      // Spawn process
      const process = await this.executionEnvironment.spawn(command, args, {
        terminal: {
          cols: tab.terminal.cols,
          rows: tab.terminal.rows,
        },
      });
      if (!this.isConnected || this.lifecycleAbortController.signal.aborted || !this.tabManager.hasTab(tab.id)) {
        process.kill();
        return;
      }

      // Set up output piping
      const outputPipeAbortController = new AbortController();
      process.output.pipeTo(
        new WritableStream({
          write: (data) => {
            tab.terminal.write(data);
          },
        }),
        { signal: outputPipeAbortController.signal }
      ).catch((error) => {
        if (!outputPipeAbortController.signal.aborted) {
          console.warn('Terminal output pipe failed:', error);
        }
      }).finally(() => {
        const currentTab = this.tabManager.getTab(tab.id);
        if (currentTab?.outputPipeAbortController === outputPipeAbortController) {
          currentTab.outputPipeAbortController = null;
        }
      });

      // Set up input for interactive processes
      const inputWriter = process.input.getWriter();
      const terminalDataDisposable = tab.terminal.onData((data) => {
        inputWriter.write(data);
      });

      // Store process reference
      this.tabManager.setTabProcess(
        tab.id,
        process,
        inputWriter,
        terminalDataDisposable,
        outputPipeAbortController
      );
      this.tabs = this.tabManager.getAllTabs();

      // Handle process exit
      process.exit.then((exitCode) => {
        this.handleProcessExit(tab.id, exitCode);
      });
    } catch (error) {
      tab.terminal.write('\x1b[31m');
      tab.terminal.write(`❌ Failed to spawn process: ${error}\r\n`);
      tab.terminal.write('\x1b[0m');
      console.error('Failed to spawn process:', error);
    }
  }

  private async getShellCommand(): Promise<IShellCommand> {
    if (this.executionEnvironment && !this.executionEnvironment.ready) {
      await this.executionEnvironment.init();
    }

    if (this.executionEnvironment?.getShellCommand) {
      return await this.executionEnvironment.getShellCommand();
    }

    return {
      command: 'jsh',
      label: 'jsh',
      prompt: '~/',
    };
  }

  private handleProcessExit(tabId: string, exitCode: number): void {
    const tab = this.tabManager.getTab(tabId);
    if (!tab) return;

    // Update tab state
    this.tabManager.markTabExited(tabId, exitCode);

    // Write exit message to terminal
    const message =
      exitCode === 0
        ? '\r\n\x1b[32m[Process completed successfully]\x1b[0m\r\n'
        : `\r\n\x1b[31m[Process exited with code ${exitCode}]\x1b[0m\r\n`;
    tab.terminal.write(message);

    // Update state to trigger re-render
    this.tabs = this.tabManager.getAllTabs();

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('process-complete', {
        bubbles: true,
        composed: true,
        detail: { tabId, exitCode },
      })
    );

    // Show actionbar to offer closing the tab (only if tab is closeable)
    if (tab.closeable) {
      this.showExitedTabActionbar(tabId, tab.label, exitCode);
    }
  }

  /**
   * Show actionbar offering to close an exited tab
   */
  private async showExitedTabActionbar(tabId: string, tabLabel: string, exitCode: number): Promise<void> {
    const isSuccess = exitCode === 0;
    const result = await this.showActionbar({
      message: isSuccess
        ? `"${tabLabel}" completed. Close tab?`
        : `"${tabLabel}" exited (code ${exitCode}). Close tab?`,
      type: isSuccess ? 'info' : 'warning',
      icon: isSuccess ? 'lucide:checkCircle' : 'lucide:alertTriangle',
      actions: [
        { id: 'close', label: 'Close Tab', primary: true },
        { id: 'keep', label: 'Keep Open' },
      ],
      timeout: { duration: 10000, defaultActionId: 'close' },
      dismissible: true,
    });

    // Close tab if user clicked "Close Tab" or timeout triggered auto-close
    if (result.actionId === 'close') {
      this.closeTab(tabId);
    }
  }

  // ========== Public API ==========

  /**
   * Create a new shell tab
   */
  public async createShellTab(label?: string): Promise<string> {
    const shellCommand = await this.getShellCommand();
    if (!this.isConnected || this.lifecycleAbortController.signal.aborted) return '';

    const tab = this.tabManager.createTab(
      {
        type: 'shell',
        label: label || `${shellCommand.label || shellCommand.command} ${this.tabManager.getTabCount() + 1}`,
        closeable: this.tabManager.getTabCount() > 0, // First tab not closeable
      },
      this.isBright
    );

    this.tabs = this.tabManager.getAllTabs();

    // Switch to new tab
    this.switchToTab(tab.id);

    // Wait for DOM update then spawn shell
    await this.updateComplete;
    if (!this.isConnected || this.lifecycleAbortController.signal.aborted) return tab.id;
    await this.spawnProcessForTab(tab, shellCommand.command, shellCommand.args || []);

    // Run setup command if this is the first tab
    if (this.tabManager.getTabCount() === 1 && this.setupCommand) {
      await this.waitForPrompt(
        tab.terminal,
        shellCommand.prompt || '~/',
        this.lifecycleAbortController.signal
      );
      if (!this.isConnected || this.lifecycleAbortController.signal.aborted) return tab.id;
      if (tab.inputWriter) {
        tab.inputWriter.write(this.setupCommand);
      }
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('tab-created', {
        bubbles: true,
        composed: true,
        detail: { tabId: tab.id },
      })
    );

    return tab.id;
  }

  /**
   * Create a terminal tab for running a process
   */
  public async createProcessTab(options: ICreateTerminalTabOptions): Promise<string> {
    const tab = this.tabManager.createTab(options, this.isBright);

    this.tabs = this.tabManager.getAllTabs();

    // Switch to new tab if requested (default: true)
    if (options.switchToTab !== false) {
      this.switchToTab(tab.id);
    }

    // Wait for DOM update
    await this.updateComplete;

    // Spawn process if command provided
    if (options.command) {
      await this.spawnProcessForTab(tab, options.command, options.args);
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('tab-created', {
        bubbles: true,
        composed: true,
        detail: { tabId: tab.id },
      })
    );

    return tab.id;
  }

  /**
   * Get the currently active tab
   */
  public getActiveTab(): ITerminalTab | null {
    if (!this.activeTabId) return null;
    return this.tabManager.getTab(this.activeTabId) || null;
  }

  /**
   * Get all tabs
   */
  public getTabs(): ITerminalTab[] {
    return this.tabManager.getAllTabs();
  }

  /**
   * Switch to a specific tab by ID
   */
  public selectTab(tabId: string): boolean {
    if (!this.tabManager.hasTab(tabId)) return false;
    this.switchToTab(tabId);
    return true;
  }

  /**
   * Close a tab by ID
   */
  public closeTab(tabId: string): boolean {
    const tab = this.tabManager.getTab(tabId);
    if (!tab || !tab.closeable) return false;

    // If closing active tab, switch to another
    if (tabId === this.activeTabId) {
      const allTabs = this.tabManager.getAllTabs();
      const currentIndex = allTabs.findIndex((t) => t.id === tabId);
      const nextTab = allTabs[currentIndex + 1] || allTabs[currentIndex - 1];

      if (nextTab) {
        this.switchToTab(nextTab.id);
      } else {
        this.activeTabId = null;
        this.detachActiveTerminal();
      }
    }

    // Close the tab
    this.tabManager.closeTab(tabId);
    this.tabs = this.tabManager.getAllTabs();

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('tab-closed', {
        bubbles: true,
        composed: true,
        detail: { tabId },
      })
    );

    return true;
  }

  /**
   * Write data to a tab's terminal
   */
  public writeToTab(tabId: string, data: string): boolean {
    const tab = this.tabManager.getTab(tabId);
    if (!tab) return false;
    tab.terminal.write(data);
    return true;
  }

  /**
   * Send input to a tab's process (if interactive)
   */
  public sendInputToTab(tabId: string, data: string): boolean {
    const tab = this.tabManager.getTab(tabId);
    if (!tab || !tab.inputWriter) return false;
    tab.inputWriter.write(data);
    return true;
  }

  /**
   * Show an actionbar notification in the terminal panel context.
   * Use this for terminal-related decisions (e.g., retry failed process, kill process, etc.)
   */
  public async showActionbar(
    options: Parameters<DeesActionbar['show']>[0]
  ): Promise<ReturnType<DeesActionbar['show']>> {
    if (!this.terminalActionbar) {
      this.terminalActionbar = this.shadowRoot?.querySelector('dees-actionbar') as DeesActionbar;
    }
    return this.terminalActionbar?.show(options);
  }

  // ========== Utility Methods ==========

  public async waitForPrompt(
    term: Terminal,
    prompt: string,
    signal?: AbortSignal,
    timeoutMs: number = 60000
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      let timeout: ReturnType<typeof setTimeout> | null = null;
      let timeoutLimit: ReturnType<typeof setTimeout> | null = null;
      const finish = () => {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        if (timeoutLimit) {
          clearTimeout(timeoutLimit);
          timeoutLimit = null;
        }
        signal?.removeEventListener('abort', finish);
        resolve();
      };

      if (signal?.aborted) {
        finish();
        return;
      }

      signal?.addEventListener('abort', finish, { once: true });
      timeoutLimit = setTimeout(finish, timeoutMs);

      const checkPrompt = () => {
        if (signal?.aborted) {
          finish();
          return;
        }
        const lines = term.buffer.active;
        for (let i = 0; i < lines.length; i++) {
          const line = lines.getLine(i);
          if (line && line.translateToString().includes(prompt)) {
            timeout = setTimeout(() => {
              finish();
            }, 100);
            return;
          }
        }
        timeout = setTimeout(checkPrompt, 100);
      };

      checkPrompt();
    });
  }

  public async setEnvironmentVariables(envArg: { [key: string]: string }): Promise<void> {
    if (!this.executionEnvironment) {
      throw new Error('No execution environment available');
    }

    let envFile = '';
    for (const key in envArg) {
      envFile += `export ${key}="${envArg[key]}"\n`;
    }

    await this.executionEnvironment.writeFile('/source.env', envFile);
  }

  /**
   * Get the underlying execution environment.
   */
  public getExecutionEnvironment(): IExecutionEnvironment | null {
    return this.executionEnvironment;
  }
}
